import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { sharingAPI } from "@/lib/api";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
  Loader2,
  Truck,
  Package,
  DollarSign,
  Image as ImageIcon,
  User,
  Share2,
  ExternalLink,
  Wrench,
  Settings,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

// Social share URLs
const SHARE_URLS = {
  facebook: (url, title) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(title)}`,
  twitter: (url, title) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  linkedin: (url, title) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  reddit: (url, title) => `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`,
};

export default function PublicBuildSheet() {
  const { shareToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    loadBuildSheet();
  }, [shareToken]);

  const loadBuildSheet = async () => {
    try {
      const { data } = await sharingAPI.getPublic(shareToken);
      setData(data);
      // Expand all categories by default
      const expanded = {};
      data.mods_by_category.forEach(cat => {
        expanded[cat.category] = true;
      });
      setExpandedCategories(expanded);
    } catch (error) {
      setError(error.response?.data?.detail || "Build sheet not found");
    } finally {
      setLoading(false);
    }
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = `Check out ${data.vehicle.nickname}'s build sheet!`;
    const shareUrl = SHARE_URLS[platform](url, title);
    window.open(shareUrl, "_blank", "width=600,height=400");
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied to clipboard!");
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="card-tactical bg-zinc-950/50 border-zinc-800 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Truck className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-foreground mb-2">Build Sheet Not Found</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Link to="/">
              <Button className="btn-tactical bg-primary text-primary-foreground">
                Go to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { vehicle, owner_name, mods_by_category, photos, stats } = data;

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="relative">
        {vehicle.primary_photo ? (
          <div className="h-64 sm:h-80 w-full overflow-hidden">
            <img
              src={vehicle.primary_photo}
              alt={vehicle.nickname}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
          </div>
        ) : (
          <div className="h-48 bg-gradient-to-b from-zinc-800 to-background" />
        )}
        
        {/* Vehicle Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-end justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-1">
                  {vehicle.nickname}
                </h1>
                <p className="text-lg text-muted-foreground">
                  {[vehicle.year, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean).join(" ")}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Built by {owner_name}</span>
                </div>
              </div>
              
              {/* Share Buttons */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyLink}
                  className="border-zinc-700 hover:bg-zinc-800"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleShare("twitter")}
                  className="border-zinc-700 hover:bg-zinc-800"
                >
                  𝕏
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleShare("facebook")}
                  className="border-zinc-700 hover:bg-zinc-800"
                >
                  f
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleShare("reddit")}
                  className="border-zinc-700 hover:bg-zinc-800"
                >
                  r/
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* QR Code + Stats Row */}
        <div className="grid gap-4 grid-cols-4 mb-8">
          <div className="flex flex-col items-center justify-center p-3 bg-white rounded-lg">
            <QRCodeSVG
              value={window.location.href}
              size={100}
              level="M"
              data-testid="public-qr-code"
            />
            <p className="text-[10px] text-zinc-500 mt-1">Scan to share</p>
          </div>
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardContent className="p-4 text-center">
              <Package className="w-6 h-6 text-primary mx-auto mb-2" />
              <p className="text-2xl font-mono font-bold text-foreground">{stats.total_mods}</p>
              <p className="text-xs text-muted-foreground">Mods</p>
            </CardContent>
          </Card>
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardContent className="p-4 text-center">
              <DollarSign className="w-6 h-6 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-mono font-bold text-foreground">
                ${stats.total_invested.toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground">Invested</p>
            </CardContent>
          </Card>
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardContent className="p-4 text-center">
              <Settings className="w-6 h-6 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-mono font-bold text-foreground">{stats.categories}</p>
              <p className="text-xs text-muted-foreground">Categories</p>
            </CardContent>
          </Card>
        </div>

        {/* Vehicle Specs */}
        {(vehicle.engine || vehicle.transmission || vehicle.tire_size || vehicle.lift_height) && (
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800 mb-8">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Wrench className="w-5 h-5 text-muted-foreground" />
                Specs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {vehicle.engine && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Engine</p>
                    <p className="text-foreground">{vehicle.engine}</p>
                  </div>
                )}
                {vehicle.transmission && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Transmission</p>
                    <p className="text-foreground">{vehicle.transmission}</p>
                  </div>
                )}
                {vehicle.transfer_case && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Transfer Case</p>
                    <p className="text-foreground">{vehicle.transfer_case}</p>
                  </div>
                )}
                {vehicle.front_axle && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Front Axle</p>
                    <p className="text-foreground">{vehicle.front_axle}</p>
                  </div>
                )}
                {vehicle.rear_axle && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Rear Axle</p>
                    <p className="text-foreground">{vehicle.rear_axle}</p>
                  </div>
                )}
                {vehicle.tire_size && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Tires</p>
                    <p className="text-foreground">{vehicle.tire_size}</p>
                  </div>
                )}
                {vehicle.lift_height && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Lift</p>
                    <p className="text-foreground">{vehicle.lift_height}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Mods by Category */}
        <div className="space-y-4 mb-8">
          <h2 className="text-xl font-bold text-foreground">Build Sheet</h2>
          
          {mods_by_category.length === 0 ? (
            <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
              <CardContent className="p-8 text-center">
                <Package className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-muted-foreground">No modifications listed yet</p>
              </CardContent>
            </Card>
          ) : (
            mods_by_category.map(({ category, mods }) => (
              <Card key={category} className="card-tactical bg-zinc-950/50 border-zinc-800">
                <button
                  className="w-full p-4 flex items-center justify-between hover:bg-zinc-900/50 transition-colors"
                  onClick={() => toggleCategory(category)}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-foreground">{category}</span>
                    <span className="text-sm text-muted-foreground">({mods.length})</span>
                  </div>
                  {expandedCategories[category] ? (
                    <ChevronUp className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                  )}
                </button>
                
                {expandedCategories[category] && (
                  <CardContent className="pt-0">
                    <div className="space-y-3 border-t border-zinc-800 pt-4">
                      {mods.map((mod) => (
                        <div
                          key={mod.id}
                          className="flex items-start justify-between py-2 border-b border-zinc-800/50 last:border-0"
                        >
                          <div className="flex-1">
                            <p className="font-medium text-foreground">{mod.item_name}</p>
                            {(mod.brand || mod.part_number) && (
                              <p className="text-sm text-muted-foreground">
                                {[mod.brand, mod.part_number].filter(Boolean).join(" • ")}
                              </p>
                            )}
                            {mod.vendor && (
                              <p className="text-xs text-muted-foreground mt-1">
                                From: {mod.vendor}
                                {mod.vendor_link && (
                                  <a
                                    href={mod.vendor_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ml-1 text-primary hover:underline inline-flex items-center"
                                  >
                                    <ExternalLink className="w-3 h-3 ml-0.5" />
                                  </a>
                                )}
                              </p>
                            )}
                          </div>
                          {mod.cost > 0 && (
                            <span className="text-sm font-mono text-green-500">
                              ${mod.cost.toLocaleString()}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            ))
          )}
        </div>

        {/* Photos */}
        {photos.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
              <ImageIcon className="w-5 h-5" />
              Photos
            </h2>
            <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
              {photos.map((photo) => (
                <div
                  key={photo.id}
                  className="aspect-square bg-zinc-900 rounded-sm overflow-hidden"
                >
                  <img
                    src={photo.url}
                    alt={photo.caption || "Vehicle photo"}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <Card className="card-tactical bg-primary/5 border-primary/30 mt-12">
          <CardContent className="p-6 text-center">
            <h3 className="text-lg font-bold text-foreground mb-2">
              Track Your Own Build
            </h3>
            <p className="text-muted-foreground mb-4">
              Create your own build sheet and maintenance tracker for free
            </p>
            <Link to="/register">
              <Button className="btn-tactical bg-primary text-primary-foreground hover:bg-primary/90">
                Get Started
              </Button>
            </Link>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-muted-foreground">
            Powered by{" "}
            <Link to="/" className="text-primary hover:underline">
              Dynamite Off-Road Buildsheet Pro
            </Link>
          </p>
        </div>
      </footer>
    </div>
  );
}
